import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Users, PlusCircle, Edit, Trash2, Save, X, UserCheck, Search } from 'lucide-react';

const Alumnos = () => {
  const navigate = useNavigate();
  const [alumnos, setAlumnos] = useState([]);
  const [apoderados, setApoderados] = useState([]); 
  const [cargando, setCargando] = useState(false);
  
  // NUEVO: Estado para el buscador
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  
  const [formData, setFormData] = useState({
    documentoIdentidad: '', nombres: '', apellidos: '', apoderadoId: '' 
  });
  const [editandoId, setEditandoId] = useState(null);

  useEffect(() => {
    cargarDatosBase();
  }, []);

  const cargarDatosBase = async () => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');
    try {
      const [resAlumnos, resApoderados] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/Alumnos`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_URL}/Apoderados`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (resAlumnos.ok) setAlumnos(await resAlumnos.json());
      if (resApoderados.ok) setApoderados(await resApoderados.json());
    } catch (e) { console.error("Error al cargar datos", e); }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setCargando(true);
    const token = localStorage.getItem('token');
    
    const url = editandoId ? `${process.env.REACT_APP_API_URL}/Alumnos/${editandoId}` : `${process.env.REACT_APP_API_URL}/Alumnos`;
    const method = editandoId ? 'PUT' : 'POST';
    
    const payload = { 
      ...formData, 
      id: editandoId || 0, 
      apoderadoId: formData.apoderadoId ? parseInt(formData.apoderadoId) : null,
      activo: true 
    };

    try {
      const res = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok || res.status === 204) {
        alert(editandoId ? 'Estudiante actualizado' : 'Estudiante registrado con éxito');
        setFormData({ documentoIdentidad: '', nombres: '', apellidos: '', apoderadoId: '' });
        setEditandoId(null);
        cargarDatosBase(); 
      } else {
        const errorText = await res.text();
        alert(`Error al guardar: \n${errorText}`);
      }
    } catch (e) { alert("Error de conexión"); } finally { setCargando(false); }
  };

  const handleEditar = (alumno) => {
    setEditandoId(alumno.id || alumno.Id);
    setFormData({
      documentoIdentidad: alumno.documentoIdentidad || alumno.DocumentoIdentidad, 
      nombres: alumno.nombres || alumno.Nombres, 
      apellidos: alumno.apellidos || alumno.Apellidos, 
      apoderadoId: alumno.apoderadoId || alumno.ApoderadoId || '' 
    });
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este estudiante?")) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Alumnos/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok || res.status === 204) cargarDatosBase();
    } catch (e) { console.error("Error al eliminar", e); }
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setFormData({ documentoIdentidad: '', nombres: '', apellidos: '', apoderadoId: '' });
  };

  // NUEVO: Lógica de filtrado en tiempo real
  const alumnosFiltrados = alumnos.filter(alumno => {
    if (!terminoBusqueda) return true;
    
    const busquedaMinuscula = terminoBusqueda.toLowerCase();
    const nombres = (alumno.nombres || alumno.Nombres || '').toLowerCase();
    const apellidos = (alumno.apellidos || alumno.Apellidos || '').toLowerCase();
    const dni = (alumno.documentoIdentidad || alumno.DocumentoIdentidad || '').toLowerCase();
    
    return nombres.includes(busquedaMinuscula) || 
           apellidos.includes(busquedaMinuscula) || 
           dni.includes(busquedaMinuscula);
  });

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <main className="flex-1 p-6 overflow-y-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="text-blue-600" /> Directorio de Estudiantes
          </h1>
          <p className="text-slate-500 mt-1">Registra a los alumnos y vincúlalos con sus padres de familia.</p>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* PANEL IZQUIERDO: FORMULARIO */}
          <div className="xl:col-span-1 bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-fit">
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 border-b pb-2">
              {editandoId ? <Edit size={20} className="text-amber-500"/> : <PlusCircle size={20} className="text-green-500"/>}
              {editandoId ? 'Editar Estudiante' : 'Nuevo Estudiante'}
            </h2>
            
            <form onSubmit={handleGuardar} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">DNI del Estudiante *</label>
                <input type="text" required value={formData.documentoIdentidad} onChange={e => setFormData({...formData, documentoIdentidad: e.target.value})} className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50" placeholder="Ej: 76543210" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Nombres *</label>
                <input type="text" required value={formData.nombres} onChange={e => setFormData({...formData, nombres: e.target.value})} className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Apellidos *</label>
                <input type="text" required value={formData.apellidos} onChange={e => setFormData({...formData, apellidos: e.target.value})} className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50" />
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <label className="flex items-center gap-1 text-xs font-bold text-blue-700 mb-2">
                  <UserCheck size={14} /> Vincular con Padre/Apoderado
                </label>
                <select 
                  value={formData.apoderadoId} 
                  onChange={e => setFormData({...formData, apoderadoId: e.target.value})} 
                  className="w-full p-2 border border-blue-200 rounded outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                >
                  <option value="">-- Sin Apoderado Asignado --</option>
                  {apoderados.map(apo => (
                    <option key={apo.id || apo.Id} value={apo.id || apo.Id}>
                      {apo.apellidos || apo.Apellidos}, {apo.nombres || apo.Nombres}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-2 flex gap-2">
                <button type="submit" disabled={cargando} className="flex-1 bg-blue-600 text-white p-2.5 rounded-lg font-bold hover:bg-blue-700 flex justify-center items-center gap-2 transition-colors">
                  <Save size={18}/> {editandoId ? 'Actualizar' : 'Guardar'}
                </button>
                {editandoId && (
                  <button type="button" onClick={cancelarEdicion} className="px-4 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300 transition-colors">
                    <X size={18}/>
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* PANEL DERECHO: BUSCADOR Y LISTA DE ALUMNOS */}
          <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-fit">
            
            {/* BARRA DE BÚSQUEDA */}
            <div className="p-4 border-b bg-slate-50">
              <div className="flex items-center border rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                <div className="pl-3 text-slate-400">
                  <Search size={18} />
                </div>
                <input 
                  type="text" 
                  value={terminoBusqueda}
                  onChange={(e) => setTerminoBusqueda(e.target.value)}
                  placeholder="Buscar estudiante por DNI, Nombre o Apellido..." 
                  className="w-full p-2.5 outline-none text-sm"
                />
                {terminoBusqueda && (
                  <button onClick={() => setTerminoBusqueda('')} className="pr-3 text-slate-400 hover:text-red-500">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse whitespace-nowrap">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-xs text-slate-500 uppercase tracking-wide">
                    <th className="p-4 font-bold">DNI</th>
                    <th className="p-4 font-bold">Estudiante</th>
                    <th className="p-4 font-bold">Apoderado Responsable</th>
                    <th className="p-4 font-bold text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {/* AQUÍ USAMOS LA LISTA FILTRADA EN LUGAR DE LA COMPLETA */}
                  {alumnosFiltrados.map(alumno => {
                    const idApoderadoDelAlumno = parseInt(alumno.apoderadoId || alumno.ApoderadoId);
                    const papa = apoderados.find(a => parseInt(a.id || a.Id) === idApoderadoDelAlumno);

                    return (
                      <tr key={alumno.id || alumno.Id} className="border-b hover:bg-slate-50 transition-colors">
                        <td className="p-4 font-medium text-slate-600">{alumno.documentoIdentidad || alumno.DocumentoIdentidad}</td>
                        <td className="p-4 font-bold text-slate-800">{alumno.apellidos || alumno.Apellidos}, {alumno.nombres || alumno.Nombres}</td>
                        <td className="p-4">
                          {papa ? (
                            <span className="text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded text-xs border border-blue-100">
                              {papa.apellidos || papa.Apellidos}, {papa.nombres || papa.Nombres}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-xs">Sin asignar</span>
                          )}
                        </td>
                        <td className="p-4 flex justify-center gap-2">
                          <button onClick={() => handleEditar(alumno)} className="p-2 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors" title="Editar"><Edit size={16}/></button>
                          <button onClick={() => handleEliminar(alumno.id || alumno.Id)} className="p-2 text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors" title="Eliminar"><Trash2 size={16}/></button>
                        </td>
                      </tr>
                    );
                  })}
                  {alumnosFiltrados.length === 0 && (
                    <tr><td colSpan="4" className="p-8 text-center text-slate-500">No se encontraron estudiantes con esa búsqueda.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Alumnos;